import Raven from 'raven-js'

import apollo from '@/graphql'
import router from '@/router'
import { Message } from 'element-ui'
import * as log from 'loglevel'

import SwitchUser from '@/graphql/auth/SwitchUser.gql'
import UserSwitcherQuery from '@/graphql/user/UserSwitcherQuery.gql'
import RestoreOriginalUserQuery from '@/graphql/auth/RestoreOriginalUser.gql'

import cache from '@/store/helpers/cache.helper'

async function wait(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

const state = {
  userSwitcherEditableFields: {
    userSelection: null,
    dialogVisible: false,
  },
  userSwitcherUsers: [],
}

const getters = {
  userSwitcher: state => state.userSwitcherEditableFields,
  userSwitcherUsers: state => state.userSwitcherUsers,
}

const mutations = {
  /**
   * Show or hide user switcher selection dialog
   * @param {boolean} visible - Dialog visibility
   */
  updateDialogVisibility(state, visible) {
    state.userSwitcherEditableFields.dialogVisible = visible
  },
  /**
   * Select a user by persisting its ID
   * @param {int} id - Selected user ID
   */
  setSelectedUser(state, id) {
    state.userSwitcherEditableFields.userSelection = id
  },
  /**
   * Update list of user switcher users
   * @param {[Object]} users - Array of switchable users
   */
  setUserSwitcherUsers(state, users) {
    // Filter out users with no name--we can't switch into them
    state.userSwitcherUsers = users.filter(u => u.name)
  },
}

const actions = {
  /**
   * Populate user switcher users
   */
  loadUserSwitcherUsers({ commit }, payload) {
    const variables = {
      offset: 0,
      limit: 10,
      sortBy: '',
      sortOrder: '',
      search: '',
      isDeleted: false,
      ...payload,
    }
    log.debug('Attempting to load user switcher users')
    apollo.defaultClient.query({
      query: UserSwitcherQuery,
      variables,
    }).then(({ data: { users } }) => {
      commit('setUserSwitcherUsers', users.list)
    }).catch((err) => {
      log.error('Could not load user switcher users:', err)
      Raven.captureException(err)
      Message({
        message: 'There was a problem loading users. Please try again.',
        type: 'error',
      })
    })
  },
  /**
   * Switch back to original user (if one exists)
   */
  restoreOriginalUser({ commit, dispatch, rootGetters }) {
    /**
     * Check if authUser is switched before attempting
     * This is considered a bad state
     */
    log.debug('Attempting to restore original user')
    if (!rootGetters['Auth/switched']) {
      log.error('Attempted to switch without a switched user. Logging out automatically')
      Message({
        message: 'Your login session has expired. Please log in again.',
        type: 'info',
      })
      dispatch('Auth/logout', { suppressLogoutMessage: true })
      return
    }
    router.push('/switch')
    apollo.defaultClient.mutate({
      mutation: RestoreOriginalUserQuery,
    }).then(async ({ data: { restoreOriginalUser: { user, iat, exp } } }) => {
      cache.bust()
      log.debug('User cookie restored successfully. Attempting to commit user to state and refresh views')
      Raven.captureBreadcrumb({
        category: 'action',
        message: `Restored original user ${user.email} (id:${user.id})`,
        data: {
          iat,
          exp,
        },
      })
      /**
       * Clear the store to remove cached results
       * see note below for known issues
       */
      await wait(2000)
      dispatch('reset')
      commit('Auth/setUser', Object.assign(user, { iat, exp, type: 'LOGIN' }))
      location.replace('/')
    })
  },
  setUserSwitcherDialogState({ commit, dispatch }, val) {
    if (val) {
      dispatch('loadUserSwitcherUsers', { limit: 0 })
    }
    commit('updateDialogVisibility', val)
  },
  setSelectedUser({ commit }, id) {
    commit('setSelectedUser', id)
  },
  /**
   * Switch into another user
   * Expects backend to set cookie and return session
   */
  switchUser({ commit, dispatch }) {
    router.push('/switch')
    apollo.defaultClient.mutate({
      mutation: SwitchUser,
      variables: {
        id: state.userSwitcherEditableFields.userSelection,
      },
    }).then(async ({ data: { switchUser: { success, code, user, iat, exp, requestingUser } } }) => {
      cache.bust()
      await wait(2000)
      dispatch('reset')
      if (success && code === 'SWITCH_SUCCESS') {
        const switchUser = user
        Object.assign(switchUser, {
          requestingUser,
        })
        commit('Auth/setUser', Object.assign(switchUser, { iat, exp, type: 'LOGIN_SWITCH' }))
        Raven.captureBreadcrumb({
          category: 'action',
          message: `Switched users from ${requestingUser.email} (id:${requestingUser.id}) ${user.email} (id:${user.id})`,
          data: {
            iat,
            exp,
          },
        })
        location.replace('/')
      }
    }).catch((err) => {
      Raven.captureException(err)
      // eslint-disable-next-line no-console
      console.log(err)
      router.push('/')
      Message({
        message: `An unexpected error occurred while switching users: ${err.message}`,
        type: 'error',
        center: false,
      })
    })
  },
}

export default {
  state,
  getters,
  mutations,
  actions,
}



// WEBPACK FOOTER //
// ./src/store/modules/auth/UserSwitcher.js